/**
 * Admin Agents API client — FE-22.
 * Calls GET /admin/agents, GET /admin/agents/:id,
 * POST /admin/agents/:id/restrict, POST /admin/agents/:id/restore.
 */

import { apiFetch } from "./client";

// ─── Types ───

export type GovernanceStatus = "active" | "restricted" | "suspended" | "banned";

export type AgentSummary = {
  readonly id: string;
  readonly name: string;
  readonly creator_id: string;
  readonly creator_name: string;
  readonly connection_mode: string;
  readonly governance_status: GovernanceStatus;
  readonly risk_score: number;
  readonly report_count: number;
  readonly created_at: string;
};

export type AuditEntry = {
  readonly id: string;
  readonly action: string;
  readonly actor_id: string;
  readonly reason: string;
  readonly created_at: string;
};

export type AgentReport = {
  readonly id: string;
  readonly reporter_id: string;
  readonly reason: string;
  readonly status: string;
  readonly created_at: string;
};

export type H2aStats = {
  readonly active: number;
  readonly suspended: number;
  readonly terminated: number;
};

export type AgentDetail = {
  readonly id: string;
  readonly name: string;
  readonly description: string | null;
  readonly creator_id: string;
  readonly creator_name: string;
  readonly connection_mode: string;
  readonly governance_status: GovernanceStatus;
  readonly risk_score: number;
  readonly report_count: number;
  readonly created_at: string;
  readonly updated_at: string;
  readonly audit_records: ReadonlyArray<AuditEntry>;
  readonly reports: ReadonlyArray<AgentReport>;
  readonly h2a_stats: H2aStats;
};

// ─── Params ───

type ListAgentsParams = {
  readonly governance_status?: GovernanceStatus;
  readonly q?: string;
  readonly limit?: number;
  readonly offset?: number;
};

type ListAgentsResponse = {
  readonly items: AgentSummary[];
  readonly total: number;
};

// ─── API calls ───

export async function fetchAgents(
  params: ListAgentsParams,
): Promise<ListAgentsResponse> {
  const sp = new URLSearchParams();
  if (params.q) sp.set("q", params.q);
  if (params.governance_status)
    sp.set("governance_status", params.governance_status);
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.offset) sp.set("offset", String(params.offset));

  const qs = sp.toString();
  const res = await apiFetch<{ data: ListAgentsResponse }>(
    qs ? `/admin/agents?${qs}` : "/admin/agents",
  );
  return res.data;
}

export async function fetchAgentDetail(id: string): Promise<AgentDetail> {
  const res = await apiFetch<{ data: AgentDetail }>(`/admin/agents/${id}`);
  return res.data;
}

export async function restrictAgent(id: string, reason: string): Promise<void> {
  await apiFetch<{ data: { restricted: boolean } }>(
    `/admin/agents/${id}/restrict`,
    {
      method: "POST",
      body: JSON.stringify({ reason }),
    },
  );
}

export async function restoreAgent(id: string, reason: string): Promise<void> {
  await apiFetch<{ data: { restored: boolean } }>(
    `/admin/agents/${id}/restore`,
    {
      method: "POST",
      body: JSON.stringify({ reason }),
    },
  );
}

export async function banAgent(id: string, reason: string): Promise<void> {
  await apiFetch<{ data: { banned: boolean } }>(`/admin/agents/${id}/ban`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}
